import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useQuery } from "react-query";
import useNotification from "../../../hooks/useNotification";
import opname from "../../../services/opname";
import queryDefault from "../../../utils/queryDefault";

interface OpnameItem {
  id: number;
  title: string;
  keyword: string;
  description: string;
  photo: string;
  price: number;
  qty_awal: number;
  qty_masuk: number;
  qty_keluar: number;
  qty_minus: number;
  ending_stock: number;
}

interface RecipeBookTabProps {
  branchId: number;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("id-ID", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export default function RecipeBookTab({ branchId }: RecipeBookTabProps) {
  const notification = useNotification();

  const { data: opnameData, isLoading, error } = useQuery(
    ["opname-recipe-book", branchId],
    () =>
      opname.getOpnameList({
        branch_id: branchId,
        type: "recipe_book",
        status: "accepted",
      }),
    {
      ...queryDefault,
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to fetch recipe books",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const items: OpnameItem[] = useMemo(() => opnameData?.data || [], [opnameData]);

  if (isLoading) return <CircularProgress />;
  if (error) return null;

  if (items.length === 0) {
    return (
      <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
        <Typography>No recipe books found</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {items.map((item) => (
        <Grid item xs={12} sm={6} md={4} key={item.id}>
          <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                {item.title}
              </Typography>
              {item.keyword && (
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 1 }}>
                  {item.keyword}
                </Typography>
              )}
              {item.description && (
                <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                  {item.description}
                </Typography>
              )}

              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Stock Awal</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.qty_awal}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Masuk</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.qty_masuk}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Keluar</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.qty_keluar}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Minus</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#d32f2f" }}>
                    {item.qty_minus}
                  </Typography>
                </Stack>
                <Box sx={{ borderTop: "1px solid #eee", pt: 1, mt: 1 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Ending Stock
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#1976d2" }}>
                      {item.ending_stock}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
